import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TabletFilterCheckbox } from '../TabletFilterCheckbox';
import { TabletFilterInput } from './TabletFilterInput';
import styles from './TabletFilterOptionsList.module.scss';
import { ReactComponent as IconClose2 } from '../img/iconClose.svg';
import { Loader, Radio } from '@mantine/core';
import ContentLoader from 'react-content-loader';

export interface SelectedItem {
  id: string;
  name: string;
}

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
  inn?: string;
}

export interface FilterOptionsListProps {
  options: FilterOption[];
  selectedValues: SelectedItem[];
  onChange: (values: SelectedItem[]) => void;
  showSearch?: boolean;
  searchPlaceholder?: string;
  className?: string;
  serverSearch?: boolean;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  hasNextPage?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  isLoading?: boolean;
  singleSelect?: boolean;
}

export const TabletFilterOptionsList: FC<FilterOptionsListProps> = ({
  options,
  selectedValues,
  onChange,
  showSearch,
  searchPlaceholder = 'Искать...',
  className,
  serverSearch,
  searchValue: controlledSearchValue,
  onSearchValueChange,
  hasNextPage,
  isFetchingMore,
  onLoadMore,
  isLoading,
  singleSelect = false,
}) => {
  const [uncontrolledSearchValue, setUncontrolledSearchValue] = useState('');
  const searchValue = controlledSearchValue ?? uncontrolledSearchValue;

  const loadMoreLockRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isFetchingMore) {
      loadMoreLockRef.current = false;
    }
  }, [isFetchingMore]);

  const handleLoadMore = useCallback(() => {
    if (!onLoadMore || !hasNextPage || isFetchingMore || loadMoreLockRef.current) {
      return;
    }
    loadMoreLockRef.current = true;
    onLoadMore();
  }, [onLoadMore, hasNextPage, isFetchingMore]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight) return;
    const gapToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = gapToBottom < 72 && el.scrollTop > 12;
    if (nearBottom) {
      handleLoadMore();
    }
  }, [handleLoadMore]);

  const shouldShowSearch = !singleSelect && showSearch !== false;

  const selectedIds = useMemo(() => {
    return selectedValues.map((item) => item.id);
  }, [selectedValues]);

  const summaryEntries = useMemo(() => {
    if (!options?.length) return [];
    return selectedValues.map((item) => ({
      value: item.id,
      label: item.name,
    }));
  }, [selectedValues]);

  const displayedSearchValue = searchValue;

  const filteredOptions = useMemo(() => {
    let list: FilterOption[];
    if (serverSearch) {
      list = options;
    } else {
      const valueForFilter = searchValue.trim().toLowerCase();
      if (!shouldShowSearch || !valueForFilter) {
        list = options;
      } else {
        list = options.filter(
          (option) => option.label && option.label.toLowerCase().includes(valueForFilter),
        );
      }
    }
    const seen = new Set<string>();
    return list.filter((opt) => {
      if (seen.has(opt.value)) return false;
      seen.add(opt.value);
      return true;
    });
  }, [options, searchValue, shouldShowSearch, serverSearch]);

  const handleOptionChange = (option: FilterOption) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      if (singleSelect) {
        if (e.target.checked) {
          onChange([{ id: option.value, name: option.label }]);
        } else {
          onChange([]);
        }
        return;
      }

      if (e.target.checked) {
        const newItem: SelectedItem = {
          id: option.value,
          name: option.label,
        };
        onChange([...selectedValues, newItem]);
      } else {
        onChange(selectedValues.filter((item) => item.id !== option.value));
      }
    };
  };

  const handleRadioChange = (value: string) => {
    if (!value) {
      onChange([]);
      return;
    }
    const option = options.find((o) => o.value === value);
    onChange([{ id: value, name: option?.label || value }]);
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
  }, [filteredOptions, hasNextPage, isFetchingMore]);

  const scrollableClassName =
    shouldShowSearch || onLoadMore ? styles.scrollableContainer : undefined;

  const handleDeleteSummary = (value: string) => {
    onChange(selectedValues.filter((item) => item.id !== value));
  };

  return (
    <div className={className} style={{ background: 'white', borderRadius: '4px' }}>
      {summaryEntries.length > 0 && (
        <div className={styles.summaryContainer}>
          {summaryEntries.map((entry) => (
            <div key={entry.value} className={styles.summaryItem}>
              <div style={{ display: 'flex', gap: '4px' }}>
                <span>{entry.label}</span>
                <IconClose2
                  onClick={() => handleDeleteSummary(entry.value)}
                  style={{ width: '16px', height: '16px' }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {shouldShowSearch && (
        <div className={styles.searchContainer}>
          <TabletFilterInput
            type='search'
            value={displayedSearchValue}
            onChange={(val) => {
              const next = String(val);
              if (controlledSearchValue !== undefined) {
                onSearchValueChange?.(next);
              } else {
                setUncontrolledSearchValue(next);
              }
            }}
            placeholder={searchPlaceholder}
            summary={summaryEntries.map((e) => e.label)}
          />
        </div>
      )}

      <div ref={scrollRef} className={scrollableClassName} onScroll={handleScroll}>
        {isLoading ? (
          <ContentLoader
            speed={2}
            height={'150px'}
            width='100%'
            className={styles.tab_loader}
            backgroundColor={'#FFFFFF'}
            foregroundColor={'#D8E4FB8C'}>
            <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
          </ContentLoader>
        ) : (
          <div className={styles.options}>
            {filteredOptions.length === 0 && searchValue && (
              <div className={styles.noOptions}>Ничего не найдено</div>
            )}
            {filteredOptions.length === 0 && !searchValue && (
              <div className={styles.noOptions}>Нет доступных опций</div>
            )}
            {singleSelect ? (
              <Radio.Group
                value={selectedIds[0] ?? ''}
                onChange={handleRadioChange}
                className={styles.radioGroup}>
                {filteredOptions.map((option) => (
                  <div key={option.value} className={styles.option}>
                    <Radio
                      color='#002CFB'
                      size='sm'
                      value={option.value}
                      label={option.label}
                      iconColor='#002CFB'
                      classNames={{
                        radio: styles.radioButton,
                        icon: styles.radioIcon,
                      }}
                      styles={{
                        label: {
                          fontSize: '16px',
                          lineHeight: '22px',
                          color: '#121922',
                          cursor: 'pointer',
                          maxWidth: '270px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                  </div>
                ))}
              </Radio.Group>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={styles.option}
                  style={{ height: option.inn ? '48px' : '40px' }}>
                  <TabletFilterCheckbox
                    checked={selectedIds.includes(option.value)}
                    onChange={handleOptionChange(option)}
                    label={option.label}
                    inn={option.inn}
                  />
                </div>
              ))
            )}
          </div>
        )}

        {isFetchingMore && hasNextPage && (
          <div className={styles.loadMoreRow}>
            <Loader size='xs' color='#002CFB' />
          </div>
        )}
      </div>
    </div>
  );
};
